import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link, useLocation, useSearchParams } from "react-router-dom";
import { Alert, Empty, Space, Spin, Typography } from "antd";
import { LockOutlined } from "@ant-design/icons";
import { getUserDetails, isAdminUser } from "../../../services/authService";
import { fetchFlightProposal } from "../../../services/flightProposalApi";
import { flightProposalTransferredToSupplier, heLabelForLdsReservationStatus } from "../../../utils/flightsNewLdsUi";
import {
  HrOrderPage,
  HrOrderHeader,
  HrOrderLayout,
  HR_ORDER_VARIANT,
  getHrOrderSectionVisibility,
} from "../../../design-system/hr-order";
import { FWPageLayout, FWSection, FWButton } from "../../../design-system/fw";
import "../../../design-system/fw/fwUiKit.css";
import "../../../design-system/fw/fwOrderProposalShell.scss";
import BookingStatusBar from "./components/BookingStatusBar";
import FlightsCombinedBlock from "./components/FlightsCombinedBlock";
import PassengerListCard from "./components/PassengerListCard";
import ExtrasCard from "./components/ExtrasCard";
import CancellationCard from "./components/CancellationCard";
import PriceSummaryCard from "./components/PriceSummaryCard";
import FlightProposalAttractionsSection from "../../../Components/FlightProposalAttractionsSection/FlightProposalAttractionsSection";
import { ReviewPageLayout, BookingStepper } from "../../../design-system/review";
import { ordersDashboardCategoryPath } from "../../../utils/ordersDashboardRoutes";
import {
  buildFlightSegmentCards,
  buildPriceSidebarLines,
  flattenPassengers,
  formatLdsYyyymmdd,
  mapSupplementToAncillaryRows,
} from "./flightProposalSummaryModel";
import { getFlightProposalDebugMockProposal } from "./flightProposalAgentPage.debugMock";
import "./FlightProposalAgentPage.scss";

const { Paragraph } = Typography;

/** גיבוי כשמבנה linked_flights לא מזוהה כקטעים */
function legacyFlatLegs(linked) {
  const flights = linked?.flight;
  if (!Array.isArray(flights)) return [];
  return flights.map((f, idx) => {
    const leg = f?.leg || f;
    const from =
      leg?.departure?.city ||
      leg?.departure?.airport ||
      leg?.from ||
      leg?.origin ||
      "—";
    const to =
      leg?.arrival?.city || leg?.arrival?.airport || leg?.to || leg?.destination || "—";
    const num = leg?.flightNum || leg?.flightNumber || leg?.flightNo || "";
    const depT = leg?.departure?.time || leg?.depTime || "";
    const arrT = leg?.arrival?.time || leg?.arrTime || "";
    return { idx: idx + 1, from, to, num, depT, arrT };
  });
}

export default function FlightProposalAgentPage() {
  const { dynamicPackageId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [proposal, setProposal] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const isDebugRoute = location.pathname.startsWith("/debug/flight-proposal");
  /** בנתיב דיבוג: mock ברירת מחדל; ‎?mock=0 — ניסיון טעינה אמיתית (אם יש session סוכן) */
  const useDebugMock = isDebugRoute && searchParams.get("mock") !== "0";

  const user = getUserDetails();
  const agentId = user && typeof user === "object" ? user.id : null;
  const admin = isAdminUser();

  const sectionVis = useMemo(() => getHrOrderSectionVisibility(HR_ORDER_VARIANT.PROPOSAL), []);

  const reload = useCallback(async () => {
    if (!dynamicPackageId || !agentId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const data = await fetchFlightProposal(dynamicPackageId, agentId, { isAdmin: admin });
      setProposal(data);
    } catch (e) {
      const st = e?.response?.status;
      let payload = e?.response?.data;
      if (typeof payload === "string") {
        try {
          payload = JSON.parse(payload);
        } catch (_) {
          payload = null;
        }
      }
      const msg =
        (payload && (payload.error || payload.message_he || payload.message)) ||
        e?.message ||
        "שגיאה בטעינת ההצעה";
      setLoadError(`${msg}${st ? ` (${st})` : ""}`);
      setProposal(null);
    } finally {
      setLoading(false);
    }
  }, [dynamicPackageId, agentId, admin]);

  const proposalFetchKeyRef = useRef("");

  useEffect(() => {
    if (isDebugRoute && useDebugMock) {
      setLoadError(null);
      setLoading(false);
      setProposal(getFlightProposalDebugMockProposal(dynamicPackageId));
      return;
    }

    if (!agentId) {
      if (!isDebugRoute) {
        navigate("/p/dashboard/home", { replace: true });
      } else {
        setLoading(false);
        setProposal(null);
        setLoadError("מצב דיבוג: הוסיפו ?mock=1 לכתובת (ברירת מחדל) או התחברו כסוכן והסירו mock כדי לטעון מהשרת.");
      }
      return;
    }

    const fetchKey = `${dynamicPackageId}|${agentId}|${admin ? "1" : "0"}`;
    if (proposalFetchKeyRef.current === fetchKey) return;
    proposalFetchKeyRef.current = fetchKey;
    reload();
  }, [
    admin,
    agentId,
    dynamicPackageId,
    isDebugRoute,
    navigate,
    reload,
    useDebugMock,
  ]);

  const offer = proposal?.offer;
  const segmentCards = useMemo(() => buildFlightSegmentCards(offer), [offer]);
  const flatLegacy = useMemo(() => legacyFlatLegs(offer?.linked_flights), [offer]);
  const pax = useMemo(() => flattenPassengers(offer?.passengers_info), [offer]);
  const ancillaryRows = useMemo(
    () => mapSupplementToAncillaryRows(offer?.supplement, pax),
    [offer?.supplement, pax]
  );
  const priceSidebar = useMemo(
    () =>
      buildPriceSidebarLines({
        total: proposal?.total_price,
        currency: proposal?.currency,
        offer,
        supplement: offer?.supplement,
      }),
    [proposal?.total_price, proposal?.currency, offer]
  );

  const transferred = proposal ? flightProposalTransferredToSupplier(proposal) : false;
  const profilePath = `/p/dashboard/dynamic/profile/${dynamicPackageId}`;
  const ordersPath = ordersDashboardCategoryPath("dynamic-packages");

  const ldsStatus = proposal?.hr_flight_module?.lds_reservation_status;
  const statusLabel =
    transferred && ldsStatus != null && String(ldsStatus).trim() !== ""
      ? heLabelForLdsReservationStatus(ldsStatus)
      : transferred
        ? "ממתין לתשלום"
        : null;

  const depRetLine =
    offer && (offer.dep_date != null || offer.ret_date != null)
      ? `יציאה ${formatLdsYyyymmdd(offer?.dep_date)} · חזרה ${formatLdsYyyymmdd(offer?.ret_date)}`
      : null;

  const summaryCardProps = {
    total: proposal?.total_price,
    currency: proposal?.currency,
    priceLines: priceSidebar.lines,
    statusLabel,
    profilePath,
    onBackOrders: () => navigate(ordersPath),
  };

  const narrowLayout =
    Boolean(loadError) || Boolean(proposal && !transferred) || Boolean(!proposal && !loading && !loadError);

  if (!isDebugRoute && (!user || !agentId)) return null;

  const reviewStepper = (
    <BookingStepper
      steps={[
        { id: "1", label: "חיפוש ובחירת טיסה" },
        { id: "2", label: "הצעה וסיכום" },
        { id: "3", label: "תשלום" },
      ]}
      activeStepId="2"
    />
  );

  return (
    <HrOrderPage
      loading={loading}
      loaded={!loading}
      narrow={narrowLayout}
      withMobileBar={Boolean(proposal && transferred)}
      className="hr-order-page--fw-shell"
    >
      <ReviewPageLayout stepper={reviewStepper}>
      <FWPageLayout>
      {isDebugRoute && useDebugMock ? (
        <div className="hr-order-page__narrow" style={{ marginBottom: "var(--hr-space-4)" }}>
          <Alert
            type="info"
            showIcon
            message="מצב debug — נתוני דמה"
            description="נתיב זמני לבדיקת UI. ללא שינוי לוגיקה עסקית. לניסיון מול השרת: ?mock=0 כשמחוברים כסוכן."
          />
        </div>
      ) : null}
      {loading ? (
        <div className="hr-order-page__spin">
          <Spin size="large" />
        </div>
      ) : loadError ? (
        <div className="hr-order-page__narrow">
          <Empty description={loadError} />
        </div>
      ) : proposal && !transferred ? (
        <div className="hr-order-page__narrow hr-order-page__gate">
          <Alert
            type="warning"
            showIcon
            message="ההזמנה טרם הועברה לספק"
            description={
              <Space direction="vertical" size="middle" style={{ inlineSize: "100%" }}>
                <Paragraph style={{ marginBlockEnd: 0 }}>
                  עמוד זה מציג סיכום הזמנה רק לאחר העברה מוצלחת לספק. השלימו את הפעולה משלב סיכום הטיסה
                  (אישור ושליחה לספק), או עברו לעמוד ההזמנה.
                </Paragraph>
                <Space wrap>
                  <FWButton variant="primary" onClick={() => navigate(profilePath)}>
                    מעבר לעמוד ההזמנה
                  </FWButton>
                  <FWButton variant="ghost" onClick={() => navigate(ordersPath)}>
                    חזרה להזמנות
                  </FWButton>
                </Space>
              </Space>
            }
          />
        </div>
      ) : proposal ? (
        <HrOrderLayout
          summaryCompact={
            sectionVis.summaryPanel ? <PriceSummaryCard variant="compact" {...summaryCardProps} /> : null
          }
          summarySidebar={
            sectionVis.summaryPanel ? <PriceSummaryCard variant="sidebar" {...summaryCardProps} /> : null
          }
          mobileBar={
            sectionVis.mobileBar ? (
              <>
                <Link to={profilePath} className="hr-order-layout__mobile-bar-link">
                  <button type="button" className="hr-order-cta hr-order-cta--primary hr-order-cta--mobile">
                    המשך לתשלום מאובטח
                  </button>
                </Link>
                <div className="hr-order-layout__mobile-bar-secure">
                  <LockOutlined aria-hidden />
                  <span>התשלום מאובטח ומוצפן</span>
                </div>
              </>
            ) : null
          }
        >
          {sectionVis.header ? (
            <HrOrderHeader>
              <BookingStatusBar packageId={dynamicPackageId} ldsOrderId={proposal.ldsorderid} />
            </HrOrderHeader>
          ) : null}

          <FWSection title="סקירה כללית" hidden={!sectionVis.overview || !depRetLine}>
            <p className="fw-ui-results-hint" style={{ margin: 0 }} translate="no">
              {depRetLine}
            </p>
          </FWSection>

          {sectionVis.travel ? (
            <FWSection title="פרטי טיסה">
              <FlightsCombinedBlock segmentCards={segmentCards} legacyLegs={flatLegacy} />
            </FWSection>
          ) : null}
          {sectionVis.passengers ? <PassengerListCard passengers={pax} /> : null}
          {sectionVis.services ? <ExtrasCard rows={ancillaryRows} currency={proposal.currency} /> : null}

          {dynamicPackageId ? (
            <FlightProposalAttractionsSection
              dynamicPackageId={dynamicPackageId}
              offer={offer}
              proposal={proposal}
              partySize={Math.max(1, pax.length || 1)}
            />
          ) : null}

          <FWSection title="פירוט מחיר" hidden={!sectionVis.pricingMain} />

          {sectionVis.notes ? <CancellationCard /> : null}
          </HrOrderLayout>
        ) : (
          <div className="hr-order-page__narrow">
            <Empty description="לא נמצאו נתונים" />
          </div>
        )}
      </FWPageLayout>
      </ReviewPageLayout>
    </HrOrderPage>
  );
}
